import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Send, AlertCircle, CheckCircle, Loader2, Bell } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface PushToken {
  id: string;
  user_id: string;
  token: string;
  platform: string;
  device_info: any;
  last_used_at: string;
  is_active: boolean;
}

export const PushNotificationTester: React.FC = () => {
  const [tokens, setTokens] = useState<PushToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedToken, setSelectedToken] = useState<string>('');
  const [title, setTitle] = useState('Test Notification');
  const [body, setBody] = useState('This is a test push notification from the admin panel.');
  const [notificationType, setNotificationType] = useState('test');

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const { data, error } = await supabase
        .from('push_tokens')
        .select('*')
        .eq('is_active', true)
        .eq('platform', 'ios')
        .order('last_used_at', { ascending: false });

      if (error) throw error;
      setTokens(data || []);
    } catch (error) {
      console.error('Error fetching push tokens:', error);
      toast.error('Failed to load push tokens');
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    if (!selectedToken || !title || !body) {
      toast.error('Please fill in all fields and select a token');
      return;
    }

    setSending(true);
    try {

      // Call the new edge function to send the test notification
      const { data, error } = await supabase.functions.invoke('send-test-push-notification', {
        body: {
          tokenId: selectedToken,
          title,
          body
        }
      });
      
      if (error) {
        console.error('Error sending test notification:', error);
        toast.error(`Failed to send test notification: ${error.message}`);
        return;
      }

      if (data?.success) {
        toast.success('Test notification sent successfully!');
        
        // Reset form
        setTitle('Test Notification');
        setBody('This is a test push notification from the admin panel.');
        setSelectedToken('');
      } else {
        console.error('Failed to send notification:', data?.error);
        toast.error(data?.error || 'Failed to send test notification');
      }
      
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error(`An unexpected error occurred: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Push Notification Tester
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading push tokens...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push Notification Tester
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {tokens.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No active push tokens found. Users need to register for push notifications first.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="space-y-2">
              <Label>Active Push Tokens ({tokens.length})</Label>
              <Select value={selectedToken} onValueChange={setSelectedToken}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a push token to test" />
                </SelectTrigger>
                <SelectContent>
                  {tokens.map((token) => (
                    <SelectItem key={token.id} value={token.id}>
                      <div className="flex items-center justify-between w-full">
                        <span className="font-mono text-xs">
                          {token.token.substring(0, 8)}...{token.token.substring(-8)}
                        </span>
                        <Badge variant="secondary" className="ml-2">
                          {token.device_info?.environment || 'unknown'}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Notification Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter notification title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Notification Type</Label>
                <Select value={notificationType} onValueChange={setNotificationType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="test">Test</SelectItem>
                    <SelectItem value="recipe">Recipe</SelectItem>
                    <SelectItem value="meal_plan">Meal Plan</SelectItem>
                    <SelectItem value="feedback">Feedback</SelectItem>
                    <SelectItem value="meal_reminder">Meal Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="body">Notification Body</Label>
              <Textarea
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Enter notification message"
                rows={3}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
              <Button 
                onClick={sendTestNotification} 
                disabled={sending || !selectedToken}
                className="flex items-center gap-2 flex-1 sm:flex-none"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {sending ? 'Sending...' : 'Send Test Notification'}
              </Button>
              
              <Button variant="outline" onClick={fetchTokens} className="flex-1 sm:flex-none">
                Refresh Tokens
              </Button>
            </div>

            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Ready:</strong> This tester now sends real push notifications via the 
                send-test-push-notification edge function with full APNS integration.
              </AlertDescription>
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
};